/*
 * Talking to the StackSchematic control plane from inside Backstage.
 *
 * This is the first thing in the viewer that calls the control plane directly, and it is what
 * the federated sign-in was built for: the visitor has already proved who they are to
 * Backstage, so they exchange Backstage's signed identity token for a StackSchematic session
 * rather than logging in twice.
 *
 * See `docs/runbooks/federated-sign-in.md`. The important half of that design, restated because
 * it is easy to lose sight of here: the exchange establishes **who**, and nothing else.
 * `authorize()` in the control plane still decides what they may do, so a Backstage admin is
 * not automatically a StackSchematic one.
 *
 * ## Session, not bearer
 *
 * The exchange sets an HttpOnly cookie and every later call rides it. The token is never kept
 * in JavaScript beyond the moment of exchange: a cookie the page cannot read is one an XSS
 * cannot steal, and the whole point of the short-lived host token is that it does not need
 * storing.
 */

export class ControlPlaneError extends Error {
  readonly status: number;

  constructor(status: number, detail: string) {
    super(detail);
    this.name = 'ControlPlaneError';
    this.status = status;
  }
}

/**
 * A one-shot store that survives a page reload, so "did we already reload to recover?" can be
 * answered after the reload. `sessionStorage` in the browser; injected in tests. Every method
 * swallows its own failure, because in a private window or a locked-down browser `sessionStorage`
 * throws on access, and a recovery mechanism must not itself become the error.
 */
export interface ReauthGuardStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

export interface ControlPlaneOptions {
  readonly baseUrl: string;
  /** Backstage's own identity token, from `identityApi.getCredentials()`. */
  readonly hostToken: string | undefined;
  readonly fetchImpl?: typeof fetch;
  /**
   * Called to recover from a stale Backstage identity token (see `#signIn`). Default: reload the
   * page, which drops the in-memory token and forces Backstage to mint a fresh one. Injected in
   * tests so the reload can be observed rather than performed.
   */
  readonly reload?: () => void;
  /** One-shot guard surviving the reload. Default: `sessionStorage`. Injected in tests. */
  readonly guardStore?: ReauthGuardStore;
}

/**
 * Set while a reload-to-recover is in flight, so a failure that survives the reload is surfaced
 * rather than reloaded on again. Cleared by a successful sign-in.
 */
const REAUTH_RELOAD_GUARD = 'stackschematic.controlPlane.reauthReloaded';

function browserGuardStore(): ReauthGuardStore {
  return {
    get: (k) => { try { return window.sessionStorage.getItem(k); } catch { return null; } },
    set: (k, v) => { try { window.sessionStorage.setItem(k, v); } catch { /* unavailable */ } },
    remove: (k) => { try { window.sessionStorage.removeItem(k); } catch { /* unavailable */ } },
  };
}

function browserReload(): void {
  // Not a browser (a unit test, or server-side): nothing to reload, and nothing that would help.
  try { window.location.reload(); } catch { /* not a browser */ }
}

/**
 * A client that signs in once, lazily, and retries a single time on a 401.
 *
 * The retry matters: a session outlives a page load but not forever, and the alternative to
 * re-exchanging is showing a logged-in user an authentication error for a login they never
 * knowingly did.
 */
export class ControlPlaneClient {
  readonly #base: string;
  readonly #hostToken: string | undefined;
  readonly #fetch: typeof fetch;
  readonly #reload: () => void;
  readonly #guard: ReauthGuardStore;
  #signedIn = false;

  constructor(options: ControlPlaneOptions) {
    this.#base = options.baseUrl.replace(/\/$/, '');
    this.#hostToken = options.hostToken;
    this.#reload = options.reload ?? browserReload;
    this.#guard = options.guardStore ?? browserGuardStore();
    /*
     * Bound, not stored bare.
     *
     * `this.#fetch = fetch` then `this.#fetch(...)` calls the global with `this` set to the
     * client, and the browser refuses: "Failed to execute 'fetch' on 'Window': Illegal
     * invocation". Node's fetch does not care about its receiver, so this passes every unit
     * test and fails the moment it runs in a browser — which is the only place it runs.
     */
    this.#fetch = options.fetchImpl ?? ((...args) => fetch(...args));
  }

  async #signIn(): Promise<void> {
    if (this.#hostToken === undefined) {
      // A guest sign-in has no verified identity and therefore no token. Saying so beats a
      // generic 401 that looks like the control plane is broken.
      throw new ControlPlaneError(
        401,
        'You are signed in to Backstage without a verified identity (guest), so StackSchematic ' +
          'cannot be told who you are. Sign in with a real identity provider.',
      );
    }
    const response = await this.#fetch(`${this.#base}/auth/host`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ token: this.#hostToken }),
    });
    if (response.status === 501) {
      throw new ControlPlaneError(
        501,
        'StackSchematic is not configured to trust this Backstage for sign-in. See ' +
          'docs/runbooks/federated-sign-in.md.',
      );
    }
    if (!response.ok) {
      const detail = await readError(response);
      // A 401 from the exchange means Backstage's identity token did not verify — and the routine
      // cause is not a real auth problem: Backstage rotates its signing keys, and a browser can
      // hold a token signed by a key already rotated out of the JWKS ("no applicable key found in
      // the JSON Web Key Set"). `getCredentials()` keeps returning that same token until it
      // expires, so the fix is a page reload: it drops the in-memory token and forces Backstage to
      // mint a fresh one against a current key — the manual remedy (a hard refresh), automated, so
      // a routine key rotation is a blink rather than a scary error mid-demo. Done at most once:
      // a 401 that survives the reload is a genuine failure and is surfaced, never looped on.
      if (response.status === 401 && this.#recoverStaleIdentity()) {
        throw new ControlPlaneError(401, 'Your Backstage session key rotated; reconnecting…');
      }
      throw new ControlPlaneError(response.status, detail);
    }
    this.#signedIn = true;
    // A clean sign-in means whatever a past reload was guarding against is resolved; re-arm it so
    // a future rotation can recover the same way.
    this.#guard.remove(REAUTH_RELOAD_GUARD);
  }

  /**
   * Reload once to shed a stale identity token. Returns true if it triggered a reload, false if a
   * reload has already been tried (guard set) — in which case the caller surfaces the error rather
   * than reloading again. The guard is left set until a successful sign-in clears it, so a
   * persistent failure across page navigations does not become a reload loop.
   */
  #recoverStaleIdentity(): boolean {
    if (this.#guard.get(REAUTH_RELOAD_GUARD) !== null) return false;
    this.#guard.set(REAUTH_RELOAD_GUARD, '1');
    this.#reload();
    return true;
  }

  async call<T>(path: string, init: RequestInit = {}): Promise<T> {
    if (!this.#signedIn) await this.#signIn();

    const send = (): Promise<Response> =>
      this.#fetch(`${this.#base}${path}`, {
        ...init,
        credentials: 'include',
        headers: { 'content-type': 'application/json', ...init.headers },
      });

    let response = await send();
    if (response.status === 401) {
      // The session expired. Re-exchange once, then give up rather than looping.
      this.#signedIn = false;
      await this.#signIn();
      response = await send();
    }

    if (!response.ok) throw new ControlPlaneError(response.status, await readError(response));
    return (await response.json()) as T;
  }
}

async function readError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string };
    // The control plane's own message is more useful than anything invented here — it says
    // which permission was missing, or which scope was rejected.
    if (typeof body.error === 'string') return body.error;
  } catch {
    // fall through
  }
  return `The control plane answered ${response.status}.`;
}
